import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import TemplateModal from '../../../../components/ArticleForms/TemplateModal';
import ArticleTable from './ArticleTable';
import GalleryArticleForm from '../../../../components/ArticleForms/GalleryArticleForm';
import GalleryTemplate from '../../../../components/ArticleTemplate/GalleryTemplate';
import { Formik, validateYupSchema } from 'formik';
import { ARTICLE_SCHEMA, TEMPLATES } from '../constant';

const ArticleWrapper = ({
  shopType = [],
  articles = [],
  loading = false,
  showForm = false,
  selectedTemplate = null,
  onTemplateSelect = () => { },
  onCloseForm = () => { },
  onSave = () => { },
  onDelete = () => { },
  onEdit = () => { },
  children = null
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [editingArticle, setEditingArticle] = React.useState(null);
  const [viewingArticle, setViewingArticle] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);

  useEffect(() => {
    const onResize = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getTemplateTitle = () => {
    if (!selectedTemplate) return 'Articles';
    const template = TEMPLATES.find(t => t.id === selectedTemplate);
    return template ? template.name : 'Create Article';
  };

  const handleCreateNew = () => {
    setEditingArticle(null);
    setShowModal(true);
  };

  const handleCloseForm = () => {
    setEditingArticle(null);
    onCloseForm();
  };

  const handleTemplateSelect = (template) => {
    onTemplateSelect(template);
    setShowModal(false);
  };

  const handleEdit = (article) => {
    const id = article.id;
    const articleToEdit = articles.find(article => article.id === id);
    if (articleToEdit) {
      // Preserve the shopType from the article being edited
      setEditingArticle({
        ...articleToEdit,
        shopType: shopType
      });
      onTemplateSelect(articleToEdit.templateType);
      onEdit(articleToEdit);
    }
  };


  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-4">
        <div className="flex align-items-center gap-3">
          {showForm && (
            <Button
              icon="pi pi-arrow-left"
              className="p-button-text p-button-text"
              onClick={handleCloseForm}
              tooltip="Back to articles"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}
          <h1 className="m-0">{getTemplateTitle()}</h1>
        </div>
        {!showForm && (
          <Button
            label={isMobile ? undefined : 'Create New Article'}
            icon="pi pi-plus"
            onClick={handleCreateNew}
            className="p-button-primary"
            aria-label="Create New Article"
            tooltip={isMobile ? 'Create New Article' : undefined}
            tooltipOptions={isMobile ? { position: 'bottom' } : undefined}
          />
        )}
      </div>

      {!showForm && (
        <ArticleTable
          articles={articles}
          loading={loading}
          onEdit={handleEdit}
          onDelete={onDelete}
          onView={(article) => {
            setViewingArticle(article);
          }}
        />
      )}

      <TemplateModal
        visible={showModal}
        templates={TEMPLATES}
        onClose={() => setShowModal(false)}
        onTemplateSelect={handleTemplateSelect}
      />

      {showForm && selectedTemplate === 'GALLERY' && (
        <div className="mt-4">
          <Formik
            initialValues={editingArticle || {
              id: '',
              title: '',
              byline: '',
              introduction: '',
              status: 'DRAFT',
              sections: [{ id: Date.now(), images: [], description: '' }],
              tags: [],
              templateType: selectedTemplate,
              ...(editingArticle ? {} : { shopType })
            }}
            enableReinitialize
            validationSchema={ARTICLE_SCHEMA}
            onSubmit={onSave}
          >
            {({ values, setFieldValue, handleSubmit, formik }) => (
              <form onSubmit={handleSubmit}>
                <GalleryArticleForm
                  onClose={onCloseForm}
                  onSave={handleSubmit}
                  values={values}
                  setFieldValue={setFieldValue}
                  formik={formik}
                />
              </form>
            )}
          </Formik>
        </div>
      )}

      {children}

      {/* View Article Dialog */}
      <Dialog
        // header={viewingArticle?.title || 'Article Preview'}
        visible={!!viewingArticle}
        onHide={() => setViewingArticle(null)}
        style={{ width: '90dvw', maxWidth: '1200px' }}
        contentStyle={{ height: '80dvh' }}
        className="p-fluid"
        maximizable={!isMobile}
        maximized={isMobile}
      >
        {viewingArticle && (
          <div style={{ height: '100%', overflowY: 'auto' }}>
            <GalleryTemplate article={viewingArticle} />
          </div>
        )}
      </Dialog>
    </div>
  );
};

ArticleWrapper.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    lastUpdated: PropTypes.string,
    content: PropTypes.string,
    author: PropTypes.string,
    category: PropTypes.string,
    template: PropTypes.string
  })),
  loading: PropTypes.bool,
  showForm: PropTypes.bool,
  selectedTemplate: PropTypes.string,
  onTemplateSelect: PropTypes.func,
  onCloseForm: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
};

ArticleWrapper.defaultProps = {
  articles: [],
  loading: false,
  showForm: false,
  selectedTemplate: null,
  onTemplateSelect: () => { },
  onCloseForm: () => { },
  onSave: () => { },
  onDelete: () => { },
  onEdit: () => { },
};

export default ArticleWrapper;
